import * as Popover from "@radix-ui/react-popover";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { type KeyboardEvent, useMemo, useRef, useState } from "react";

import { cn } from "../../utils";
import styles from "./multi-filter-dropdown.module.css";

export type MultiFilterOption<T extends string> = { value: T; label: string };
export type MultiFilterMode = "include" | "exclude";

type CommonProps<T extends string> = {
  options: MultiFilterOption<T>[];
  label: string;
  triggerLabel: string;
  searchPlaceholder?: string;
};

type MultipleProps<T extends string> = CommonProps<T> & {
  selectedValues: readonly T[];
  onSelectedValuesChange: (values: T[]) => void;
  modes?: never;
  onModesChange?: never;
};

type IncludeExcludeProps<T extends string> = CommonProps<T> & {
  modes: ReadonlyMap<T, MultiFilterMode>;
  onModesChange: (modes: Map<T, MultiFilterMode>) => void;
  selectedValues?: never;
  onSelectedValuesChange?: never;
};

export type MultiFilterDropdownProps<T extends string> = MultipleProps<T> | IncludeExcludeProps<T>;

function usesIncludeExclude<T extends string>(props: MultiFilterDropdownProps<T>): props is IncludeExcludeProps<T> {
  return props.modes !== undefined;
}

/** A searchable multi-select. Supplying `modes` enables include/exclude/unpicked cycling. */
export function MultiFilterDropdown<T extends string>(props: MultiFilterDropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const filteredOptions = useMemo(
    () => props.options.filter((option) => option.label.toLowerCase().includes(query.trim().toLowerCase())),
    [props.options, query],
  );
  const isIncludeExclude = usesIncludeExclude(props);

  const closeDropdown = () => {
    setQuery("");
    setHighlightedIndex(0);
    setOpen(false);
  };

  const modeFor = (value: T): MultiFilterMode | undefined => isIncludeExclude ? props.modes.get(value) : props.selectedValues.includes(value) ? "include" : undefined;

  const toggleOption = (value: T) => {
    if (isIncludeExclude) {
      const next = new Map(props.modes);
      const mode = next.get(value);
      if (mode === "include") next.set(value, "exclude");
      else if (mode === "exclude") next.delete(value);
      else next.set(value, "include");
      props.onModesChange(next);
      return;
    }
    props.onSelectedValuesChange(
      props.selectedValues.includes(value)
        ? props.selectedValues.filter((selected) => selected !== value)
        : [...props.selectedValues, value],
    );
  };

  const clear = () => {
    if (isIncludeExclude) props.onModesChange(new Map());
    else props.onSelectedValuesChange([]);
    triggerRef.current?.focus();
  };

  const focusOption = (index: number) => optionRefs.current[index]?.focus();
  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      focusOption(Math.min(highlightedIndex, filteredOptions.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      focusOption(Math.max(highlightedIndex - 1, 0));
    } else if (event.key === "Home") {
      event.preventDefault();
      setHighlightedIndex(0);
      focusOption(0);
    } else if (event.key === "End") {
      event.preventDefault();
      const index = filteredOptions.length - 1;
      setHighlightedIndex(index);
      focusOption(index);
    } else if (event.key === "Enter" && filteredOptions[highlightedIndex]) {
      event.preventDefault();
      toggleOption(filteredOptions[highlightedIndex].value);
    } else if (event.key === "Escape") {
      event.preventDefault();
      closeDropdown();
    }
  };
  const handleOptionKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      focusOption(Math.min(index + 1, filteredOptions.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      (index === 0 ? searchInputRef.current : optionRefs.current[index - 1])?.focus();
    } else if (event.key === "Home") {
      event.preventDefault();
      focusOption(0);
    } else if (event.key === "End") {
      event.preventDefault();
      focusOption(filteredOptions.length - 1);
    } else if (event.key === "Escape") {
      event.preventDefault();
      closeDropdown();
    }
  };
  const selectedCount = isIncludeExclude ? props.modes.size : props.selectedValues.length;

  return (
    <Popover.Root open={open} onOpenChange={(next) => { setOpen(next); if (!next) { setQuery(""); setHighlightedIndex(0); } }}>
      <div className={styles.control}>
        <Popover.Trigger asChild>
          <button ref={triggerRef} type="button" className={styles.trigger} aria-label={props.label}>
            {props.triggerLabel}<ChevronDown className={styles.triggerIcon} aria-hidden="true" />
          </button>
        </Popover.Trigger>
        {selectedCount > 0 && <button type="button" className={styles.clearButton} onClick={(event) => { event.stopPropagation(); clear(); }} aria-label={`Clear ${props.label}`}><X className={styles.clearIcon} aria-hidden="true" /></button>}
      </div>
      <Popover.Portal>
        <Popover.Content side="bottom" align="start" sideOffset={6} className={styles.content} onOpenAutoFocus={(event) => { event.preventDefault(); searchInputRef.current?.focus(); }} onCloseAutoFocus={(event) => { event.preventDefault(); triggerRef.current?.focus(); }}>
          <label className={styles.search}><Search size={14} aria-hidden="true" /><input ref={searchInputRef} value={query} onChange={(event) => { setQuery(event.target.value); setHighlightedIndex(0); }} placeholder={props.searchPlaceholder ?? "Search options"} aria-label={`Search ${props.label}`} onKeyDown={handleSearchKeyDown} /></label>
          <ul className={styles.list} role="listbox" aria-label={props.label} aria-multiselectable="true">
            {filteredOptions.map((option, index) => {
              const mode = modeFor(option.value);
              return <li key={option.value}><button ref={(element) => { optionRefs.current[index] = element; }} type="button" role="option" aria-selected={mode !== undefined} aria-label={`${option.label}: ${mode === "include" ? "Include" : mode === "exclude" ? "Exclude" : "Not selected"}`} onKeyDown={(event) => handleOptionKeyDown(event, index)} className={cn(styles.option, index === highlightedIndex && styles.optionHighlighted, mode === "include" && styles.optionInclude, mode === "exclude" && styles.optionExclude)} onFocus={() => setHighlightedIndex(index)} onMouseMove={() => setHighlightedIndex(index)} onClick={() => toggleOption(option.value)}><span className={styles.optionLabel}>{option.label}</span>{mode === "include" && <Check className={styles.includeIcon} aria-hidden="true" />}{mode === "exclude" && <X className={styles.excludeIcon} aria-hidden="true" />}</button></li>;
            })}
          </ul>
          {filteredOptions.length === 0 && <p className={styles.empty} role="status">No options found</p>}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
